import type { I18n } from "@lingui/core";

import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { comparer, reaction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { Column } from "../../../components/ui-kit/tables/data-table/types";
import type { MeasurementRow, SelectorName } from "./measurements";

import { PermissionBit } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { afibDisclaimerMsg } from "../../../app/common-messages";
import { errorText } from "../../../app/error-text";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { NativeSelect } from "../../../components/ui-kit/base/native-select";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { Switch } from "../../../components/ui-kit/fields/switch";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { ppgConditionText } from "../../../constants/enum-to-text";
import { hasPermission } from "../../../constants/permissions";
import { PatientSubpage } from "../common/patient-subpage";
import { ECGPlot } from "../ecg/ecg-plot";
import {
  BloodPressureCell,
  DateCell,
  EcgActionCell,
  QualityCell,
  QualityLegend,
  QuickLookCell,
  RhythmCell,
  SignalValueCell,
  ValueCellView,
  VARIANT_LABELS,
} from "./measurement-cells";
import {
  MEASUREMENT_CATEGORIES,
  type MeasurementColumnSpec,
  orderedColumnSpecs,
} from "./measurement-columns";
import { MeasurementDialog } from "./measurement-dialog";
import {
  DAYS_PER_PAGE,
  Measurements,
  type MeasurementsUrlState,
  SELECTOR_NAMES,
} from "./measurements";

/**
 * One row per measured day, with the day's sessions as expandable sub-rows.
 * The API decides which columns exist and pages the days; this page decides
 * how they look.
 */
export const MeasurementsRoute = observer(function MeasurementsRoute(props: {
  app: AppModel;
}) {
  const { t, i18n } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [searchParams, setSearchParams] = useSearchParams();

  const [model] = useState(
    () =>
      new Measurements(api, patient.patientId, {
        pageIndex: pageIndexFromUrl(searchParams),
        ...urlStateFrom(searchParams),
      }),
  );
  const [plotUuid, setPlotUuid] = useState<string | null>(null);
  const [detailRow, setDetailRow] = useState<MeasurementRow | null>(null);

  useEffect(() => {
    void model.load();
  }, [model]);

  // The URL follows what the table shows, so a link reopens the same view.
  // Read and written live: DataTable writes `page` into the same query.
  useEffect(
    () =>
      reaction(
        () => model.urlState,
        (state) => {
          if (!state) return;
          const current = window.location.search.replace(/^\?/, "");
          const next = withUrlState(new URLSearchParams(current), state);
          if (next.toString() !== current) {
            setSearchParams(next, { replace: true });
          }
        },
        { fireImmediately: true, equals: comparer.structural },
      ),
    [model, setSearchParams],
  );

  const columnKeys = model.columns.map((column) => column.key).join(",");
  const columns = useMemo(
    () =>
      buildColumns({
        model,
        specs: orderedColumnSpecs(columnKeys ? columnKeys.split(",") : []),
        i18n,
        dateLabel: t`Date`,
        downloadBase: api.versionedRoute(),
        // Without ECG_RAW the API returns the recording without its samples,
        // so there would be nothing to plot.
        onOpenPlot: hasPermission(api.permissions, PermissionBit.ECG_RAW)
          ? setPlotUuid
          : null,
        onOpenDetail: setDetailRow,
      }),
    // `i18n.locale` re-labels the headers on a locale switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model, columnKeys, i18n.locale, api],
  );

  if (!model.response && model.error) {
    return (
      <PatientSubpage title={t`Measurements`}>
        <ErrorMessage message={errorText(model.error)} />
      </PatientSubpage>
    );
  }

  return (
    <PatientSubpage title={t`Measurements`} loading={!model.response}>
      <ECGPlot uuid={plotUuid} onClose={() => setPlotUuid(null)} />
      {detailRow && (
        <MeasurementDialog
          model={model}
          row={detailRow}
          onClose={() => setDetailRow(null)}
        />
      )}
      {model.error ? <ErrorMessage message={errorText(model.error)} /> : null}
      <DataTable
        // A new filter changes which days exist, so the table starts over on
        // page 1; a new column set scopes its remembered column visibility.
        key={`${model.fullOnly}|${columnKeys}`}
        persistState
        persistStateKey={columnKeys}
        model={model.table}
        itemKeyField="id"
        columns={columns}
        rowsPerPage={DAYS_PER_PAGE}
        isLoading={model.loading}
        getSubRows={(row) => row.subRows}
        serverPagination={{
          rowCount: model.totalDays,
          onChange: (pagination) => model.setPagination(pagination),
        }}
        filterNoun={(count) =>
          t`${plural(count, { one: "day", other: "days" })}`
        }
        filterActions={
          <Toolbar
            model={model}
            onFullOnlyChange={(fullOnly) => {
              // A server-side filter changes which days exist: back to page 1.
              const next = new URLSearchParams(window.location.search);
              next.delete("page");
              setSearchParams(next, { replace: true });
              model.setFullOnly(fullOnly);
            }}
          />
        }
      />
    </PatientSubpage>
  );
});

const Toolbar = observer(function Toolbar({
  model,
  onFullOnlyChange,
}: {
  model: Measurements;
  onFullOnlyChange: (fullOnly: boolean) => void;
}) {
  const { t } = useLingui();
  const selectors: { name: SelectorName; label: string }[] = [
    { name: "hr_algorithm", label: t`HR algorithm` },
    { name: "hr_condition", label: t`HR condition` },
    { name: "spo2_condition", label: t`SpO₂ condition` },
    { name: "af_model", label: t`AF model` },
  ];

  return (
    <div className="flex items-center gap-3 whitespace-nowrap">
      {selectors.map(({ name, label }) => {
        const options = model.selectorOptions(name);
        if (options.length === 0) return null;
        return (
          <label key={name} className="flex items-center gap-1 text-sm">
            {label}
            <NativeSelect
              className="w-auto"
              value={model.selected(name) ?? ""}
              onChange={(event) => model.choose(name, event.target.value)}
            >
              {options.map((option) => (
                <option key={option} value={option}>
                  {selectorOptionText(option)}
                </option>
              ))}
            </NativeSelect>
          </label>
        );
      })}
      <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
        <Switch value={model.fullOnly} onChange={onFullOnlyChange} />
        <Trans>Full measurements only</Trans>
      </label>
    </div>
  );
});

function buildColumns({
  model,
  specs,
  i18n,
  dateLabel,
  downloadBase,
  onOpenPlot,
  onOpenDetail,
}: {
  model: Measurements;
  specs: MeasurementColumnSpec[];
  i18n: I18n;
  dateLabel: string;
  downloadBase: string;
  onOpenPlot: ((ecgUuid: string) => void) | null;
  onOpenDetail: (row: MeasurementRow) => void;
}): Column<MeasurementRow>[] {
  const name = (spec: MeasurementColumnSpec) =>
    typeof spec.label === "string" ? spec.label : i18n._(spec.label);
  // Where the header can't render (the column-visibility list), the unit
  // still tells dLVFP from its median.
  const label = (spec: MeasurementColumnSpec) =>
    spec.unit ? `${name(spec)} (${spec.unit})` : name(spec);

  const firstPpgKey = specs.find((spec) => spec.category === "ppg")?.key;

  const date: Column<MeasurementRow> = {
    header: dateLabel,
    sticky: "left",
    align: "left",
    width: 170,
    cell: ({ item }) => <DateCell row={item} />,
  };

  return [
    date,
    ...specs.map(
      (spec): Column<MeasurementRow> => ({
        header: <ColumnHeader model={model} spec={spec} label={name(spec)} />,
        label: label(spec),
        group: i18n._(MEASUREMENT_CATEGORIES[spec.category]),
        groupHeader:
          spec.category === "ppg" ? (
            <span className="inline-flex items-center gap-0.5">
              {i18n._(MEASUREMENT_CATEGORIES.ppg)}
              <HelperTooltip
                content={i18n._(
                  msg`Click a row anywhere in these columns to open the session's detail: HR, SpO₂, AF probability, SQI and quality flag per position. Where you may edit them, you can change a quality flag or add a comment there.`,
                )}
              />
            </span>
          ) : undefined,
        sticky: spec.category === "actions" ? "right" : undefined,
        width: 110,
        // The PPG derived group is one click target for the session's detail
        // dialog — padding included, with nothing but the cursor to show it.
        // Without signals (none readable, or no PPG that session) there is
        // nothing to open.
        cellAction:
          spec.category === "ppg"
            ? (item) =>
                (item.session.ppg_signals?.length ?? 0) > 0
                  ? () => onOpenDetail(item)
                  : null
            : undefined,
        cell: ({ item }) => {
          const content = (
            <MeasurementCellView
              model={model}
              row={item}
              spec={spec}
              downloadBase={downloadBase}
              onOpenPlot={onOpenPlot}
            />
          );
          // One keyboard stop per row rather than one per value: the group's
          // first cell holds a bare button whose click bubbles to the cell's
          // action, so Enter opens the same dialog a click anywhere does.
          return spec.key === firstPpgKey &&
            (item.session.ppg_signals?.length ?? 0) > 0 ? (
            <button
              type="button"
              aria-haspopup="dialog"
              className="cursor-pointer rounded-sm bg-transparent p-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              {content}
            </button>
          ) : (
            content
          );
        },
      }),
    ),
  ];
}

function MeasurementCellView({
  model,
  row,
  spec,
  downloadBase,
  onOpenPlot,
}: {
  model: Measurements;
  row: MeasurementRow;
  spec: MeasurementColumnSpec;
  downloadBase: string;
  onOpenPlot: ((ecgUuid: string) => void) | null;
}) {
  const session = row.session;
  const cell = session.cells?.[spec.key];
  switch (spec.key) {
    case "hr":
    case "spo2":
    case "af_probability":
      return (
        <SignalValueCell
          model={model}
          session={session}
          spec={{ ...spec, key: spec.key }}
        />
      );
    case "quality":
      return <QualityCell session={session} />;
    case "bp":
      return <BloodPressureCell cell={cell} spec={spec} />;
    case "ecg_rhythm":
      return <RhythmCell cell={cell} />;
    case "quicklook_ppg":
      return <QuickLookCell model={model} row={row} />;
    case "open_ecg":
      return (
        <EcgActionCell
          session={session}
          downloadBase={downloadBase}
          onOpenPlot={onOpenPlot}
        />
      );
    default:
      return <ValueCellView cell={cell} spec={spec} />;
  }
}

/**
 * The measurement's name, with its qualifier — unit and, for the
 * selector-driven columns, the selected variant, which is part of what the
 * column shows — always on a line of its own below it, so every titled unit
 * sits in the same place however wide its column is. Neither line breaks
 * within itself. A column shown only because the range has data for it — it
 * isn't in the patient's configuration — is marked.
 */
const ColumnHeader = observer(function ColumnHeader({
  model,
  spec,
  label,
}: {
  model: Measurements;
  spec: MeasurementColumnSpec;
  label: string;
}) {
  const { t } = useLingui();
  const column = model.columns.find((item) => item.key === spec.key);
  const { qualifier, alternatives } = columnQualifiers(model, spec);

  return (
    <span className="inline-flex flex-col items-center">
      <span className="whitespace-nowrap">
        {label}
        {column && !column.configured ? (
          <HelperTooltip
            className="ml-0.5"
            content={
              <Trans>
                Not in this patient's measurement configuration. Shown because
                there are readings in this period.
              </Trans>
            }
          />
        ) : null}
        {spec.key === "quality" ? (
          <HelperTooltip className="ml-0.5" content={<QualityLegend />} />
        ) : null}
        {spec.key === "af_probability" ? (
          <HelperTooltip className="ml-0.5" content={t(afibDisclaimerMsg)} />
        ) : null}
      </span>
      {qualifier ? (
        // The alternatives lie hidden under the selected qualifier, so the
        // line is always as wide as the widest of them: the table sizes its
        // columns to their content, and a switch to a shorter or longer one
        // would otherwise resize the column, and the table with it.
        <span className="inline-grid whitespace-nowrap font-normal text-muted-foreground">
          <span className="col-start-1 row-start-1">
            {/* Read as one title, "HR (bpm)", not two words run together. */}
            <span className="sr-only"> </span>({qualifier})
          </span>
          {[...alternatives].map((alternative) => (
            <span
              key={alternative}
              className="invisible col-start-1 row-start-1"
            >
              ({alternative})
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
});

/**
 * The column's qualifier under the selected variant and condition, and every
 * other one the toolbar can switch it to.
 */
function columnQualifiers(
  model: Measurements,
  spec: MeasurementColumnSpec,
): { qualifier: string; alternatives: Set<string> } {
  const selectors = model.columnSelectors(spec.key);
  const qualifierFor = (variant?: string, condition?: string) => {
    const choice = [
      variant !== undefined ? selectorOptionText(variant) : undefined,
      condition !== undefined ? ppgConditionText(condition) : undefined,
    ]
      .filter(Boolean)
      .join(" · ");
    return [spec.unit, choice].filter(Boolean).join(", ");
  };
  // The selected value is among the options except where it is the payload's
  // default and the range has no data for it, and the other selector's
  // options still pair with it then.
  const reachable = (selector?: SelectorName): (string | undefined)[] =>
    selector
      ? [model.selected(selector), ...model.selectorOptions(selector)]
      : [undefined];

  const qualifier = qualifierFor(
    selectors.variant && model.selected(selectors.variant),
    selectors.condition && model.selected(selectors.condition),
  );
  const alternatives = new Set(
    reachable(selectors.variant).flatMap((variant) =>
      reachable(selectors.condition).map((condition) =>
        qualifierFor(variant, condition),
      ),
    ),
  );
  alternatives.delete(qualifier);
  return { qualifier, alternatives };
}

function selectorOptionText(option: string): string {
  return VARIANT_LABELS[option] ?? ppgConditionText(option);
}

/** URL names of the view state, camelCase like the table's own `pageSize`. */
const URL_KEYS: Record<SelectorName, string> = {
  hr_algorithm: "hrAlgorithm",
  hr_condition: "hrCondition",
  spo2_condition: "spo2Condition",
  af_model: "afModel",
};

function urlStateFrom(searchParams: URLSearchParams): MeasurementsUrlState {
  const choices: MeasurementsUrlState["choices"] = {};
  for (const name of SELECTOR_NAMES) {
    const value = searchParams.get(URL_KEYS[name]);
    if (value) choices[name] = value;
  }
  return { choices };
}

/** Only what differs from the defaults is written; the rest is removed. */
function withUrlState(
  searchParams: URLSearchParams,
  state: MeasurementsUrlState,
): URLSearchParams {
  for (const name of SELECTOR_NAMES) {
    const value = state.choices[name];
    if (value === undefined) searchParams.delete(URL_KEYS[name]);
    else searchParams.set(URL_KEYS[name], value);
  }
  return searchParams;
}

/** The page DataTable will restore from the URL, for the first request. */
function pageIndexFromUrl(searchParams: URLSearchParams): number {
  const page = parseInt(searchParams.get("page") ?? "", 10);
  return isNaN(page) ? 0 : Math.max(page - 1, 0);
}
